import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { twMerge } from 'tailwind-merge';
import { resourceLinkPlugin } from 'src/features/chat/components/MarkdownWithCitations';
import { ResourceLink as ResourceLinkComponent } from 'src/features/chat/components/ResourceLink';
import type { GroupOverviewResponse } from '../api';

export default function GroupsOverviewMarkdown({
  body,
  resourceLinks,
  groupId,
  className,
  onResourceLinkClick
}: {
  body: string;
  resourceLinks: GroupOverviewResponse['resource_links'];
  groupId?: string;
  className?: string;
  onResourceLinkClick?: (link: NonNullable<GroupOverviewResponse['resource_links']>[number]) => void;
}) {
  return (
    <div
      className={twMerge(
        'flex flex-col gap-3 prose max-w-none text-sm text-content-default prose-p:my-0 prose-ul:my-0 prose-ol:my-0 prose-li:my-0',
        className
      )}
    >
      <ReactMarkdown
        remarkPlugins={[resourceLinkPlugin, remarkGfm]}
        components={
          {
            ul: ({ children }: { children?: ReactNode }) => (
              <ul className="m-0 flex list-none flex-col gap-2 p-0">{children}</ul>
            ),
            ol: ({ children }: { children?: ReactNode }) => (
              <ol className="m-0 flex list-none flex-col gap-2 p-0">{children}</ol>
            ),
            li: ({ children }: { children?: ReactNode }) => <li className="m-0 p-0 leading-5">{children}</li>,
            p: ({ children }: { children?: ReactNode }) => <p className="m-0 leading-5">{children}</p>,
            resourceLink: ({ node }: { node: { properties?: { resourceId?: string; content?: string } } }) => {
              const resourceId = node.properties?.resourceId;
              const content = node.properties?.content;
              const data = (resourceLinks ?? []).find((link) => link.id === resourceId);
              if (!data || !content) {
                return <span>{content}</span>;
              }
              return (
                <ResourceLinkComponent
                  content={content}
                  data={data}
                  variant="prose"
                  groupId={groupId}
                  trackAsChat={!onResourceLinkClick}
                  onActivate={onResourceLinkClick ? () => onResourceLinkClick(data) : undefined}
                />
              );
            }
          } as any
        }
      >
        {body}
      </ReactMarkdown>
    </div>
  );
}
